import type { TranslatedString } from '@interfaces/shared';
import { isValidEmail } from '@utils/common';
import type { FormSignerMapping } from './formSignerMapping';

export type SignerRowSignature = {
  signerKey: string;
  signerUserId?: string | null;
  signerUser?: {
    id?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    primaryEmail?: string | null;
  } | null;
};

export type SignerRowFormCompletion = {
  id: string;
  translatedName?: TranslatedString;
  requiredSignatures: SignerRowSignature[];
};

export type SignerRowSignerType = {
  key: string;
  translatedName: TranslatedString;
  defaultSigningOrder?: number;
  roleProfiles?: { id: string }[];
};

export type DefaultSignerUser = {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

export type BuildSignerRowsInput = {
  formCompletions: SignerRowFormCompletion[];
  signerTypes: SignerRowSignerType[];
  savedSignerMapping: FormSignerMapping[];
  defaultSignerUser?: DefaultSignerUser;
  userBoundFallbackLabel: string;
};

const DEFAULT_USER_BOUND_SIGNING_ORDER = 1;

const firstFilled = (...values: (string | null | undefined)[]) =>
  values.find((value) => !!value) ?? undefined;

export const collectSignerKeys = (
  formCompletions: SignerRowFormCompletion[],
) => {
  const signerKeys = formCompletions.flatMap((formCompletion) =>
    formCompletion.requiredSignatures.map((signature) => signature.signerKey),
  );
  return [...new Set(signerKeys)];
};

type SignatureGroup = {
  signatures: SignerRowSignature[];
  documentNames: TranslatedString[];
};

const groupSignaturesByKey = (formCompletions: SignerRowFormCompletion[]) => {
  const groups = new Map<string, SignatureGroup>();
  for (const formCompletion of formCompletions) {
    // One entry for each form completion, also when it has several signatures with the same key.
    const keysInFormCompletion = new Set<string>();
    for (const signature of formCompletion.requiredSignatures) {
      const group = groups.get(signature.signerKey) ?? {
        signatures: [],
        documentNames: [],
      };
      group.signatures.push(signature);
      if (
        formCompletion.translatedName &&
        !keysInFormCompletion.has(signature.signerKey)
      ) {
        group.documentNames.push(formCompletion.translatedName);
      }
      keysInFormCompletion.add(signature.signerKey);
      groups.set(signature.signerKey, group);
    }
  }
  return groups;
};

const buildUserBoundRow = ({
  signerKey,
  signatures,
  saved,
  userBoundFallbackLabel,
}: {
  signerKey: string;
  signatures: SignerRowSignature[];
  saved?: FormSignerMapping;
  userBoundFallbackLabel: string;
}): FormSignerMapping => {
  const signerUserId = signatures.find(
    (signature) => signature.signerUserId,
  )?.signerUserId;
  const signerUser = signatures.find(
    (signature) => signature.signerUser,
  )?.signerUser;
  const label = [signerUser?.firstName, signerUser?.lastName]
    .filter(Boolean)
    .join(' ');

  return {
    signerKey,
    signerUserId: signerUserId ?? undefined,
    signerLabel: label || userBoundFallbackLabel,
    firstName: firstFilled(saved?.firstName, signerUser?.firstName),
    lastName: firstFilled(saved?.lastName, signerUser?.lastName),
    email: firstFilled(saved?.email, signerUser?.primaryEmail),
    useOrgUser: false,
    signingOrder: saved?.signingOrder ?? DEFAULT_USER_BOUND_SIGNING_ORDER,
  };
};

const buildStaticRow = ({
  signerKey,
  signerType,
  saved,
  defaultSignerUser,
}: {
  signerKey: string;
  signerType: SignerRowSignerType;
  saved?: FormSignerMapping;
  defaultSignerUser?: DefaultSignerUser;
}): FormSignerMapping => {
  const signer =
    saved ??
    (defaultSignerUser && {
      firstName: defaultSignerUser.firstName,
      lastName: defaultSignerUser.lastName,
      email: defaultSignerUser.email,
      organizationUserId: defaultSignerUser.id,
    });

  return {
    signerKey,
    signerName: signerType.translatedName,
    firstName: signer?.firstName,
    lastName: signer?.lastName,
    email: signer?.email,
    organizationUserId: signer?.organizationUserId,
    useOrgUser: true,
    signingOrder: signerType.defaultSigningOrder,
    roleProfiles: signerType.roleProfiles,
  };
};

export const buildSignerRows = ({
  formCompletions,
  signerTypes,
  savedSignerMapping,
  defaultSignerUser,
  userBoundFallbackLabel,
}: BuildSignerRowsInput) => {
  const rows: FormSignerMapping[] = [];

  for (const [signerKey, { signatures, documentNames }] of groupSignaturesByKey(
    formCompletions,
  )) {
    const saved = savedSignerMapping.find(
      (signer) => signer.signerKey === signerKey,
    );
    const isUserBound = signatures.some((signature) => signature.signerUserId);

    if (isUserBound) {
      rows.push({
        ...buildUserBoundRow({
          signerKey,
          signatures,
          saved,
          userBoundFallbackLabel,
        }),
        documentNames,
      });
      continue;
    }

    const signerType = signerTypes.find((type) => type.key === signerKey);
    if (!signerType) continue;
    rows.push({
      ...buildStaticRow({ signerKey, signerType, saved, defaultSignerUser }),
      documentNames,
    });
  }

  return rows;
};

export const getDocumentNamesLabel = (
  documentNames: TranslatedString[],
  translate: (name: TranslatedString) => string,
) => documentNames.map((name) => translate(name)).join(', ');

export const isSignerRowComplete = (row: FormSignerMapping) => {
  if (!row.signerUserId) return true;
  return (
    !!row.firstName?.trim() &&
    !!row.lastName?.trim() &&
    isValidEmail(row.email?.trim())
  );
};

export const stripDisplayFields = ({
  documentNames: _documentNames,
  ...signer
}: FormSignerMapping): FormSignerMapping => signer;
